import * as React from "react";
import { StatusPill } from "@ozpanel/kit";

/**
 * How a domain's delegation state is rendered — in ONE place, because it was in two and they
 * disagreed.
 *
 * The DNS module page graded the server's `mismatch_severity` correctly. The DNS table on
 * /admin/web did not: it treated ANY non-empty `mismatch` string as a fault and painted it red
 * "not delegated here". For oztag.click the server was saying, in the very field being ignored,
 * severity=info and "Our own nameservers are answering for this domain — it is served here and
 * working." The console rendered that as a red failure — not a missing message, the OPPOSITE of
 * the message it had.
 *
 * Only two of the five states this can describe are faults. Painting the commonest benign one red
 * teaches an operator to skim past the row that has genuinely lost their mail, which is the entire
 * value of the column.
 *
 * The first fix went into one copy. The second copy kept the old behaviour for as long as nobody
 * opened that page. Shared now so there is no second copy to forget.
 */

export type DelegationRow = {
  mismatch?: string;
  mismatch_severity?: "info" | "warn" | "critical" | "";
};

export function severityRank(sev?: string): number {
  switch (sev) {
    case "critical":
      return 3;
    case "warn":
      return 2;
    case "info":
      return 1;
    default:
      return 0;
  }
}

/** True only for states an operator must act on — used for counts and banners, which were
 *  counting benign rows as problems and announcing "N zones are not delegated to this platform"
 *  about zones this platform is serving. */
export function isDelegationFault(d: DelegationRow): boolean {
  if (!d.mismatch) return false;
  const sev = d.mismatch_severity || "warn";
  return sev === "warn" || sev === "critical";
}

export function DelegationCell({ d }: { d: DelegationRow }) {
  if (!d.mismatch) return <StatusPill tone="ok">delegated here</StatusPill>;
  const sev = d.mismatch_severity || "warn";
  if (sev === "info") {
    return (
      <span title={d.mismatch} className="text-[11.5px] text-muted">
        served here — nothing to sync
      </span>
    );
  }
  return (
    <span title={d.mismatch}>
      <StatusPill tone={sev === "critical" ? "bad" : "warn"}>
        {sev === "critical" ? "the world sees a different zone" : "binding does not match reality"}
      </StatusPill>
    </span>
  );
}
